import { Injectable } from '@angular/core';
import { FirebaseX } from '@awesome-cordova-plugins/firebase-x/ngx';
import { Platform } from '@ionic/angular';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, skipWhile, switchMap, take } from 'rxjs';
import { RemoteConfigProperties } from 'src/app/core/services/analytics/models/remote-config-properties';
import { FeatureFlagFacade } from 'src/app/core/state/facades/feature-flag.facade';
import { environment } from 'src/environments/environment';

type SuccessHandlerFunction = (config: RemoteConfigProperties) => void;
type ErrorHandlerFunction = (error: string) => void;

const DEFAULT_MINIMUM_FETCH_INTERVAL = environment.environmentName === 'production' ? 900 : 1;

const ERROR_MESSAGES = {
  config: '[Remote Config] was unable to set configuration due to the error:',
  fetch: '[Remote Config] was unable to fetch config due to the error:',
  get: '[Remote Config] was unable to get values due to the error:',
} as const;

/**
 * This service enables Firebase Remote Config capabilities.
 */

@Injectable({
  providedIn: 'root',
})
export class RemoteConfigService {
  /**
   * For storing initialisation status or Remote Config.
   */
  private initialisedSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * For storing remote config.
   */
  private remoteConfigSubject: ReplaySubject<RemoteConfigProperties> = new ReplaySubject(1);

  constructor(
    private firebaseX: FirebaseX,
    private platform: Platform,
    private featureFlagFacade: FeatureFlagFacade,
  ) {}

  /**
   * Getter for initialisation status.
   */
  get isInitialised$(): Observable<boolean> {
    return this.initialisedSubject.asObservable();
  }

  /**
   * Getter for remote config observable.
   */
  get remoteConfig$(): Observable<RemoteConfigProperties> {
    return this.isInitialised$.pipe(
      skipWhile((isInitialised) => !isInitialised),
      switchMap(() => this.remoteConfigSubject),
    );
  }

  /**
   * Begins remote config initialisation sequence.
   */
  initialise(): void {
    if (this.platform.is('cordova')) {
      combineLatest({
        featureFlag: this.featureFlagFacade.isFeatureEnabled$(['core__remote_config']),
        isInitialised: this.isInitialised$,
      })
        .pipe(take(1))
        .subscribe(({ featureFlag, isInitialised }) => {
          if (featureFlag.core__remote_config && !isInitialised) {
            this.platform.ready().then(() => this.getRemoteConfig());
          }
        });
    }
  }

  /**
   * Configures and fetches remote config from firebase. Firebase default minimum fetch interval is 12 hours.
   * However, we set this interval to 15 mins. Max rate allowed by Google is 5 times / per hour / per device.
   *
   * @param fetchTimeout - fetch timeout in seconds. Default is 60 seconds.
   * @param minimumFetchInterval - minimum fetch interval in seconds.
   */
  getRemoteConfig(fetchTimeout = 60, minimumFetchInterval = DEFAULT_MINIMUM_FETCH_INTERVAL): void {
    this.firebaseX.setConfigSettings(
      fetchTimeout,
      minimumFetchInterval,
      () => {
        this.firebaseX.fetchAndActivate(() => {
          this.firebaseX.getAll(this.successHandler(), this.errorHandler('get'));
        }, this.errorHandler('fetch'));
      },
      this.errorHandler('config'),
    );
  }

  /**
   * Helper callback function to update Subject with remote config properties.
   */
  private successHandler(): SuccessHandlerFunction {
    const initialisedSubject = this.initialisedSubject;
    const remoteConfigSubject = this.remoteConfigSubject;
    return (config: RemoteConfigProperties): void => {
      remoteConfigSubject.next(config);
      initialisedSubject.next(true);
    };
  }

  /**
   * Helper callback function to construct and output human-friendly error messages.
   *
   * @param stage - stage at which a given error occurred.
   */
  private errorHandler(stage: keyof typeof ERROR_MESSAGES): ErrorHandlerFunction {
    return (error: string): void => console.error(ERROR_MESSAGES[stage], error);
  }
}
